import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Avatar,
  LinearProgress, Chip, IconButton, Tooltip, Button,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Stack, Paper, Divider, CircularProgress,
  Alert, Collapse, Slider, Badge, Checkbox,
  ToggleButtonGroup, ToggleButton, Snackbar
} from '@mui/material';
import {
  PersonOutline, Warning, CheckCircle, TrendingUp,
  AutoAwesome, Refresh, Settings, ExpandMore, ExpandLess,
  Assignment, AccessTime, Speed, ArrowForward, PlayArrow,
  CheckCircleOutline, FilterList, Close
} from '@mui/icons-material';
import { workloadService } from '../services/api';

// ─── Rendu markdown simplifié ─────────────────────────────────────────────────

function SimpleMarkdown({ children }) {
  if (!children) return null;
  return (
    <Box>
      {children.split('\n').map((line, i) => {
        const bold = line.replace(/\*\*(.*?)\*\*/g, (_, t) => `<b>${t}</b>`);
        const isH2 = line.startsWith('## ');
        const isLi = line.startsWith('- ') || line.startsWith('* ');
        if (isH2) return <Typography key={i} variant="subtitle2" fontWeight={700} sx={{ mt: 1.5, mb: 0.5 }}>{line.replace('## ', '')}</Typography>;
        if (isLi) return (
          <Box key={i} sx={{ display: 'flex', gap: 1, mb: 0.25 }}>
            <Typography variant="body2" component="span">•</Typography>
            <Typography variant="body2" dangerouslySetInnerHTML={{ __html: bold.replace(/^[*-]\s/, '') }} />
          </Box>
        );
        if (!line.trim()) return <Box key={i} sx={{ height: 4 }} />;
        return <Typography key={i} variant="body2" dangerouslySetInnerHTML={{ __html: bold }} sx={{ mb: 0.25 }} />;
      })}
    </Box>
  );
}

// ─── Constantes ───────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  overloaded: { color: '#ef4444', bg: '#fef2f2', label: 'Surchargé',  icon: '🔴' },
  loaded:     { color: '#f59e0b', bg: '#fffbeb', label: 'Chargé',     icon: '🟡' },
  normal:     { color: '#10b981', bg: '#f0fdf4', label: 'Normal',     icon: '🟢' },
  available:  { color: '#6b7280', bg: '#f9fafb', label: 'Disponible', icon: '⚪' },
};

const PRIORITY_COLOR = {
  urgent: '#ef4444', high: '#f59e0b', medium: '#3b82f6', low: '#6b7280'
};

const PRIORITY_LABEL = {
  urgent: 'Urgente', high: 'Haute', medium: 'Moyenne', low: 'Basse'
};

const FILTER_OPTIONS = [
  { value: 'all',        label: 'Tous' },
  { value: 'overloaded', label: '🔴 Surchargés' },
  { value: 'loaded',     label: '🟡 Chargés' },
  { value: 'normal',     label: '🟢 Normal' },
  { value: 'available',  label: '⚪ Disponibles' },
];

// ─── Carte membre ─────────────────────────────────────────────────────────────

function MemberCard({ member, onConfigCapacity }) {
  const [expanded, setExpanded] = useState(false);
  const cfg      = STATUS_CONFIG[member.load_status] || STATUS_CONFIG.available;
  const barColor = member.utilization >= 100 ? 'error'
    : member.utilization >= 80 ? 'warning'
    : 'success';

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        borderColor: cfg.color + '60',
        bgcolor: cfg.bg,
        transition: 'box-shadow .2s',
        '&:hover': { boxShadow: 3 }
      }}
    >
      <CardContent sx={{ pb: '12px !important' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            badgeContent={
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: cfg.color, border: '2px solid white' }} />
            }
          >
            <Avatar sx={{ bgcolor: cfg.color + '25', color: cfg.color, fontWeight: 700, width: 44, height: 44 }}>
              {member.name ? member.name.charAt(0).toUpperCase() : '?'}
            </Avatar>
          </Badge>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={700} noWrap>{member.name}</Typography>
            <Typography variant="caption" color="text.secondary" noWrap>{member.email}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            <Chip
              label={`${cfg.icon} ${cfg.label}`}
              size="small"
              sx={{ bgcolor: cfg.color + '20', color: cfg.color, fontWeight: 600, border: `1px solid ${cfg.color}40` }}
            />
            <Tooltip title="Configurer la capacité">
              <IconButton size="small" onClick={() => onConfigCapacity(member)}>
                <Settings fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Barre d'utilisation */}
        <Box sx={{ mb: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Utilisation : <strong>{member.estimated_hours}h</strong> / {member.weekly_hours}h
            </Typography>
            <Typography variant="caption" fontWeight={700} sx={{ color: cfg.color }}>
              {member.utilization}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={Math.min(member.utilization, 100)}
            color={barColor}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>

        {/* Stats */}
        <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Assignment sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              <strong>{member.tasks.length}</strong> tâche{member.tasks.length > 1 ? 's' : ''}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AccessTime sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              <strong>{member.logged_hours}h</strong> loguées
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Speed sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              Cap. <strong>{member.weekly_hours}h</strong>/sem.
            </Typography>
          </Box>
        </Stack>

        {/* Accordéon tâches */}
        {member.tasks.length > 0 && (
          <>
            <Divider sx={{ my: 1 }} />
            <Button
              size="small"
              endIcon={expanded ? <ExpandLess /> : <ExpandMore />}
              onClick={() => setExpanded(v => !v)}
              sx={{ px: 0, color: 'text.secondary', fontWeight: 500, fontSize: '0.75rem' }}
            >
              {expanded ? 'Masquer' : 'Voir'} les tâches ({member.tasks.length})
            </Button>
            <Collapse in={expanded}>
              <Stack spacing={0.5} sx={{ mt: 1 }}>
                {member.tasks.map(t => (
                  <Box
                    key={t.id}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1,
                      px: 1, py: 0.5, borderRadius: 1,
                      bgcolor: 'rgba(0,0,0,0.03)',
                    }}
                  >
                    <Box sx={{
                      width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                      bgcolor: PRIORITY_COLOR[t.priority] || '#6b7280'
                    }} />
                    <Typography variant="caption" sx={{ flex: 1 }} noWrap>{t.title}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                      {t.estimated_hours}h
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Collapse>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Carte d'action de rééquilibrage ──────────────────────────────────────────

function ActionCard({ action, checked, onChange }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 1.5,
        borderColor: checked ? '#6366f1' : 'divider',
        bgcolor: checked ? '#f5f3ff' : 'background.paper',
        transition: 'all .15s',
        cursor: 'pointer',
      }}
      onClick={onChange}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Checkbox
          checked={checked}
          onChange={onChange}
          size="small"
          sx={{ p: 0, mt: 0.25, color: '#6366f1', '&.Mui-checked': { color: '#6366f1' } }}
          onClick={e => e.stopPropagation()}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap sx={{ mb: 0.25 }}>
            {action.task_title}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              {action.from_name}
            </Typography>
            <ArrowForward sx={{ fontSize: 12, color: 'text.disabled' }} />
            <Typography variant="caption" color="success.main" fontWeight={600}>
              {action.to_name}
            </Typography>
            <Typography variant="caption" color="text.disabled">·</Typography>
            <Typography variant="caption" color="text.secondary">
              {action.task_hours}h
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            <Chip
              label={PRIORITY_LABEL[action.task_priority] || action.task_priority}
              size="small"
              sx={{
                height: 18,
                fontSize: '0.65rem',
                bgcolor: PRIORITY_COLOR[action.task_priority] + '20',
                color: PRIORITY_COLOR[action.task_priority],
                fontWeight: 600,
              }}
            />
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
              {action.to_name} : {action.to_utilization}% → ~{action.new_to_utilization}%
            </Typography>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
}

// ─── Composant principal ──────────────────────────────────────────────────────

export default function WorkloadView({ projectId }) {
  const [workload,         setWorkload]         = useState([]);
  const [stats,            setStats]            = useState(null);
  const [loading,          setLoading]          = useState(true);
  const [aiLoading,        setAiLoading]        = useState(false);
  const [suggestion,       setSuggestion]       = useState('');
  const [actions,          setActions]          = useState([]);
  const [showAI,           setShowAI]           = useState(false);
  const [selectedActions,  setSelectedActions]  = useState(new Set());
  const [applying,         setApplying]         = useState(false);
  const [filterStatus,     setFilterStatus]     = useState('all');
  const [capDialog,        setCapDialog]        = useState(false);
  const [capTarget,        setCapTarget]        = useState(null);
  const [capValue,         setCapValue]         = useState(40);
  const [saving,           setSaving]           = useState(false);
  const [error,            setError]            = useState('');
  const [snackbar,         setSnackbar]         = useState({ open: false, message: '', severity: 'success' });

  // ─── Chargement ────────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await workloadService.getWorkload(projectId);
      setWorkload(res.data.workload || []);
      setStats(res.data.stats || null);
    } catch (e) {
      setError('Impossible de charger la charge de travail.');
      console.error(e);
    }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  // ─── Rééquilibrage IA ──────────────────────────────────────────────────────

  async function handleRebalance() {
    setAiLoading(true);
    setSuggestion('');
    setActions([]);
    setSelectedActions(new Set());
    setShowAI(true);
    try {
      const res = await workloadService.rebalance(projectId);
      setSuggestion(res.data.suggestion || '');
      const receivedActions = res.data.actions || [];
      setActions(receivedActions);
      // Tout sélectionner par défaut
      setSelectedActions(new Set(receivedActions.map((_, i) => i)));
      if (res.data.workload) setWorkload(res.data.workload);
    } catch (e) {
      setSuggestion('❌ Impossible de contacter ERA Assistant. Vérifiez votre connexion.');
      console.error(e);
    }
    setAiLoading(false);
  }

  // ─── Sélection des actions ────────────────────────────────────────────────

  function toggleAction(index) {
    setSelectedActions(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }

  function toggleAll() {
    if (selectedActions.size === actions.length) {
      setSelectedActions(new Set());
    } else {
      setSelectedActions(new Set(actions.map((_, i) => i)));
    }
  }

  // ─── Application des réassignations ──────────────────────────────────────

  async function handleApply() {
    if (selectedActions.size === 0) return;
    setApplying(true);
    try {
      const toApply = [...selectedActions].map(i => ({
        task_id:      actions[i].task_id,
        from_user_id: actions[i].from_user_id,
        to_user_id:   actions[i].to_user_id,
      }));
      const res = await workloadService.applyRebalance(projectId, toApply);
      setWorkload(res.data.workload || []);
      setSnackbar({ open: true, message: res.data.message, severity: res.data.errors?.length > 0 ? 'warning' : 'success' });
      // Retirer les actions appliquées
      const remaining = actions.filter((_, i) => !selectedActions.has(i));
      setActions(remaining);
      setSelectedActions(new Set());
      if (remaining.length === 0) setShowAI(false);
    } catch (e) {
      setSnackbar({ open: true, message: 'Erreur lors de l\'application des réassignations.', severity: 'error' });
      console.error(e);
    }
    setApplying(false);
  }

  // ─── Capacité ─────────────────────────────────────────────────────────────

  function openCapacity(member) {
    setCapTarget(member);
    setCapValue(member.weekly_hours || 40);
    setCapDialog(true);
  }

  async function saveCapacity() {
    if (!capTarget) return;
    setSaving(true);
    try {
      await workloadService.updateCapacities(projectId, [{ user_id: capTarget.id, weekly_hours: capValue }]);
      setCapDialog(false);
      setSnackbar({ open: true, message: `Capacité de ${capTarget.name} mise à jour.`, severity: 'success' });
      load();
    } catch (e) {
      console.error(e);
    }
    setSaving(false);
  }

  // ─── Filtrage ─────────────────────────────────────────────────────────────

  const filteredWorkload = filterStatus === 'all'
    ? workload
    : workload.filter(m => m.load_status === filterStatus);

  // ─── Stats pills ──────────────────────────────────────────────────────────

  const statChips = stats ? [
    { label: `${stats.totalTasks} tâches actives`,   color: 'default',  icon: <Assignment sx={{ fontSize: 14 }} /> },
    { label: `${Math.round(stats.totalEstimated)}h estimées`, color: 'default', icon: <AccessTime sx={{ fontSize: 14 }} /> },
    { label: `${stats.overloaded} surchargé${stats.overloaded !== 1 ? 's' : ''}`, color: stats.overloaded > 0 ? 'error' : 'success', icon: <Warning sx={{ fontSize: 14 }} /> },
    { label: `${stats.available} disponible${stats.available !== 1 ? 's' : ''}`,  color: 'success', icon: <CheckCircle sx={{ fontSize: 14 }} /> },
  ] : [];

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <Box>
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingUp color="primary" />
            Charge de travail
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Visualisez qui est surchargé, configurez les capacités et laissez ERA rééquilibrer l'équipe.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Tooltip title="Actualiser">
            <IconButton onClick={load} disabled={loading}><Refresh /></IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={aiLoading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <AutoAwesome />}
            onClick={handleRebalance}
            disabled={aiLoading || loading}
            sx={{
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              '&:hover': { background: 'linear-gradient(135deg, #4f46e5, #7c3aed)' },
              '&:disabled': { background: '#d1d5db' }
            }}
          >
            {aiLoading ? 'Analyse en cours…' : 'Rééquilibrer avec ERA'}
          </Button>
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* ── Stats globales ─────────────────────────────────────────────────── */}
      {stats && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
          {statChips.map(c => (
            <Chip key={c.label} label={c.label} icon={c.icon} size="small" color={c.color} variant="outlined" />
          ))}
        </Stack>
      )}

      {/* ── Suggestion IA + Actions ────────────────────────────────────────── */}
      <Collapse in={showAI}>
        <Paper
          variant="outlined"
          sx={{ p: 3, mb: 3, borderRadius: 2, borderColor: '#8b5cf6', bgcolor: '#f5f3ff', position: 'relative' }}
        >
          {/* Bouton fermer */}
          <IconButton
            size="small"
            onClick={() => { setShowAI(false); setActions([]); setSelectedActions(new Set()); }}
            sx={{ position: 'absolute', top: 8, right: 8, color: 'text.secondary' }}
          >
            <Close fontSize="small" />
          </IconButton>

          {/* Titre */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <AutoAwesome sx={{ color: '#8b5cf6' }} />
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#6d28d9' }}>
              ERA Assistant — Analyse de la charge de travail
            </Typography>
          </Box>

          {aiLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
              <CircularProgress size={20} sx={{ color: '#8b5cf6' }} />
              <Typography variant="body2" color="text.secondary">
                ERA analyse la charge de travail de votre équipe…
              </Typography>
            </Box>
          ) : (
            <>
              {/* Texte IA */}
              {suggestion && (
                <Box sx={{ mb: actions.length > 0 ? 3 : 0 }}>
                  <SimpleMarkdown>{suggestion}</SimpleMarkdown>
                </Box>
              )}

              {/* ── Actions structurées ────────────────────────────────────── */}
              {actions.length > 0 && (
                <>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#6d28d9', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PlayArrow sx={{ fontSize: 16 }} />
                      {actions.length} action{actions.length > 1 ? 's' : ''} suggérée{actions.length > 1 ? 's' : ''}
                    </Typography>
                    <Button
                      size="small"
                      variant="text"
                      onClick={toggleAll}
                      sx={{ color: '#6d28d9', fontSize: '0.75rem' }}
                    >
                      {selectedActions.size === actions.length ? 'Tout désélectionner' : 'Tout sélectionner'}
                    </Button>
                  </Box>

                  <Stack spacing={1} sx={{ mb: 2 }}>
                    {actions.map((action, i) => (
                      <ActionCard
                        key={i}
                        action={action}
                        checked={selectedActions.has(i)}
                        onChange={() => toggleAction(i)}
                      />
                    ))}
                  </Stack>

                  {/* Bouton appliquer */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      startIcon={applying ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <CheckCircleOutline />}
                      onClick={handleApply}
                      disabled={selectedActions.size === 0 || applying}
                      sx={{
                        background: selectedActions.size > 0
                          ? 'linear-gradient(135deg, #10b981, #059669)'
                          : undefined,
                        '&:hover': { background: 'linear-gradient(135deg, #059669, #047857)' },
                      }}
                    >
                      {applying
                        ? 'Application en cours…'
                        : `Appliquer ${selectedActions.size} réassignation${selectedActions.size > 1 ? 's' : ''}`
                      }
                    </Button>
                    {selectedActions.size === 0 && (
                      <Typography variant="caption" color="text.secondary">
                        Sélectionnez au moins une action pour appliquer.
                      </Typography>
                    )}
                  </Box>
                </>
              )}

              {/* Aucune action possible */}
              {!aiLoading && actions.length === 0 && suggestion && (
                <Box sx={{ mt: 2, p: 1.5, borderRadius: 1, bgcolor: '#ecfdf5', border: '1px solid #6ee7b7' }}>
                  <Typography variant="body2" color="success.dark" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CheckCircle sx={{ fontSize: 16 }} />
                    Aucune réassignation automatique possible. Appliquez les suggestions manuellement.
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Paper>
      </Collapse>

      {/* ── Filtres de statut ──────────────────────────────────────────────── */}
      {workload.length > 0 && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <FilterList sx={{ fontSize: 18, color: 'text.secondary' }} />
          <ToggleButtonGroup
            value={filterStatus}
            exclusive
            onChange={(_, v) => { if (v !== null) setFilterStatus(v); }}
            size="small"
            sx={{ flexWrap: 'wrap', gap: 0.5 }}
          >
            {FILTER_OPTIONS.map(opt => {
              const count = opt.value === 'all'
                ? workload.length
                : workload.filter(m => m.load_status === opt.value).length;
              return (
                <ToggleButton
                  key={opt.value}
                  value={opt.value}
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    fontSize: '0.75rem',
                    borderRadius: '20px !important',
                    border: '1px solid',
                    borderColor: 'divider',
                    '&.Mui-selected': {
                      bgcolor: '#6366f1',
                      color: 'white',
                      borderColor: '#6366f1',
                      '&:hover': { bgcolor: '#4f46e5' }
                    }
                  }}
                >
                  {opt.label} {count > 0 && <Box component="span" sx={{ ml: 0.5, opacity: 0.7 }}>({count})</Box>}
                </ToggleButton>
              );
            })}
          </ToggleButtonGroup>
        </Box>
      )}

      {/* ── Grille des membres ─────────────────────────────────────────────── */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : workload.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 5, textAlign: 'center', borderStyle: 'dashed', borderRadius: 3 }}>
          <PersonOutline sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">Aucun membre avec des tâches actives.</Typography>
        </Paper>
      ) : filteredWorkload.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <Typography color="text.secondary">
            Aucun membre avec le statut « {FILTER_OPTIONS.find(o => o.value === filterStatus)?.label} ».
          </Typography>
          <Button size="small" onClick={() => setFilterStatus('all')} sx={{ mt: 1 }}>
            Voir tous les membres
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {filteredWorkload.map(member => (
            <Grid item xs={12} sm={6} md={4} key={member.id}>
              <MemberCard member={member} onConfigCapacity={openCapacity} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* ── Légende ────────────────────────────────────────────────────────── */}
      {workload.length > 0 && (
        <Stack
          direction="row" spacing={2} flexWrap="wrap" useFlexGap
          sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}
        >
          {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
            <Box
              key={key}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
              onClick={() => setFilterStatus(filterStatus === key ? 'all' : key)}
            >
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: cfg.color }} />
              <Typography
                variant="caption"
                color={filterStatus === key ? cfg.color : 'text.secondary'}
                fontWeight={filterStatus === key ? 700 : 400}
              >
                {cfg.icon} {cfg.label}
              </Typography>
            </Box>
          ))}
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            Cliquez sur une légende pour filtrer
          </Typography>
        </Stack>
      )}

      {/* ── Dialog capacité ────────────────────────────────────────────────── */}
      <Dialog open={capDialog} onClose={() => setCapDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Capacité de {capTarget?.name}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Définissez le nombre d'heures disponibles par semaine pour calculer l'utilisation.
          </Typography>
          <Typography variant="subtitle2" gutterBottom>
            {capValue}h / semaine
          </Typography>
          <Slider
            value={capValue}
            onChange={(_, v) => setCapValue(v)}
            min={1}
            max={80}
            step={1}
            marks={[
              { value: 20, label: '20h' },
              { value: 40, label: '40h' },
              { value: 60, label: '60h' },
            ]}
            valueLabelDisplay="auto"
            color="primary"
            sx={{ mt: 1 }}
          />
          <TextField
            fullWidth
            size="small"
            label="Ou saisir manuellement"
            type="number"
            value={capValue}
            onChange={e => setCapValue(Math.max(1, Math.min(168, Number(e.target.value))))}
            sx={{ mt: 3 }}
            InputProps={{ inputProps: { min: 1, max: 168 } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCapDialog(false)}>Annuler</Button>
          <Button variant="contained" onClick={saveCapacity} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar feedback ──────────────────────────────────────────────── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
